import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { Spin, Button, Typography, Input, Form, Modal, Select } from 'antd';
import {
  EyeTwoTone,
  LoadingOutlined,
  EyeInvisibleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
// Api
import { GET_USERS } from 'api/users/queries';
import {
  ADMIN_CHANGE_PASSWORD,
  EDIT_USER,
  UPDATE_USER_ROLE,
} from 'api/users/mutations';
// Types
import {
  AdminChangePassword,
  AdminChangePasswordVariables,
} from 'api/users/types/AdminChangePassword';
import { Users } from 'api/users/types/Users';
import {
  AccountSubType,
  StoreStatus,
  UserRole,
} from 'api/graphql-global-types';
import { EditUser, EditUserVariables } from 'api/users/types/EditUser';
import {
  UpdateUserRole,
  UpdateUserRoleVariables,
} from 'api/users/types/UpdateUserRole';
// Helpers
import { validationRules } from 'pages/ManageAccount/helpers';
// Hooks
import { useDefaultCustomerRedirectOnEmptyId } from 'hooks';
// UI
import { errorNotification, successNotification } from 'ui/Notification';
// Styles
import './styles.scss';

const { Text, Title } = Typography;
const { confirm } = Modal;

const formItemLayout = {
  labelCol: { span: 3 },
  wrapperCol: { span: 3 },
};

const ROLE_OPTIONS = [
  { value: UserRole.Customer, label: 'Customer (fan)' },
  { value: UserRole.Agency, label: 'Agency (brand)' },
  { value: UserRole.ContentCreator, label: 'Content Creator (talent)' },
];

const formatEnumLabel = (value: string): string =>
  value.replace(/([a-z])([A-Z])/g, '$1 $2');

const CustomerSettings: React.FC = () => {
  useDefaultCustomerRedirectOnEmptyId();
  const { customerId } = useParams<{ customerId: string | undefined }>();
  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(true);
  const [isChangePasswordDisabled, setIsChangePasswordDisabled] =
    useState<boolean>(true);
  const [newRole, setNewRole] = useState<UserRole | undefined>();
  const [newSubtype, setNewSubtype] = useState<AccountSubType | undefined>();
  const [formEditUser] = Form.useForm();
  const [formChangePassword] = Form.useForm();
  const {
    data,
    loading: userLoading,
    refetch,
  } = useQuery<Users>(GET_USERS, {
    variables: {
      input: { id: customerId, status: [StoreStatus.Active] },
    },
    fetchPolicy: 'cache-and-network',
  });
  const [editUser, { loading }] = useMutation<EditUser, EditUserVariables>(
    EDIT_USER
  );
  const [changePassword] = useMutation<
    AdminChangePassword,
    AdminChangePasswordVariables
  >(ADMIN_CHANGE_PASSWORD);
  const [updateUserRole, { loading: updateRoleLoading }] = useMutation<
    UpdateUserRole,
    UpdateUserRoleVariables
  >(UPDATE_USER_ROLE);

  const customerData = data?.users?.entities?.[0];

  const initialValues = {
    email: customerData?.email,
    firstName: customerData?.firstName,
    lastName: customerData?.lastName,
    phoneNumber: customerData?.phoneNumber,
  };

  const editUserHandler = () => {
    try {
      const values = formEditUser.getFieldsValue();
      const valuesToSend = {
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumberE164: values.phoneNumber || null,
        id: customerData?.id,
      };
      editUser({ variables: { input: valuesToSend } });
      successNotification('The data is updated');
    } catch (e) {
      errorNotification();
      console.log('Error occured', { ...e });
    }
  };

  const changePasswordHandler = async () => {
    confirm({
      title: `Are you sure you want to change a password for this user?`,
      icon: <ExclamationCircleOutlined />,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          await changePassword({
            variables: {
              input: {
                id: customerId || '',
                newPassword: formChangePassword.getFieldValue('password'),
              },
            },
          });
          successNotification('Password is changed');
        } catch (e) {
          errorNotification();
          console.log('adminChangePassword error:', { ...e });
        }
      },
    });
  };

  const currentRole = customerData?.role;

  // ContentCreator can be re-selected to change only the subtype
  const isChangeRoleDisabled =
    !currentRole ||
    !newRole ||
    (newRole === currentRole && newRole !== UserRole.ContentCreator) ||
    (newRole === UserRole.ContentCreator && !newSubtype);

  const changeRoleHandler = () => {
    const roleLabel =
      ROLE_OPTIONS.find((option) => option.value === newRole)?.label || newRole;

    confirm({
      title: `Are you sure you want to change this user's role to ${roleLabel}?`,
      icon: <ExclamationCircleOutlined />,
      content:
        newRole === UserRole.ContentCreator
          ? 'The account will be moved to Pending status and will need to go through talent approval. The user will be logged out.'
          : 'The user will be logged out and will need to log in again.',
      okText: 'Change Role',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await updateUserRole({
            variables: {
              input: {
                id: customerId || '',
                newRole: newRole as UserRole,
                ...(newRole === UserRole.ContentCreator && {
                  subtype: newSubtype,
                }),
              },
            },
          });
          successNotification('User role is updated');
          setNewRole(undefined);
          setNewSubtype(undefined);
          await refetch();
        } catch (e) {
          errorNotification((e as Error)?.message || undefined);
          console.log('updateUserRole error:', e);
        }
      },
    });
  };

  const checkValues = (): void => {
    const values = {
      ...formEditUser.getFieldsValue(),
      id: customerId,
    };
    setIsSubmitDisabled(
      Object.values(values).some((x) => x === null || x === '') ||
        formEditUser.getFieldsError()?.some(({ errors }) => errors?.length > 0)
    );
  };

  const isPasswordMatching = () =>
    formChangePassword.getFieldValue('password') ===
    formChangePassword.getFieldValue('confirmPassword');

  const checkPasswordValues = (): void => {
    setIsChangePasswordDisabled(
      Object.values(formChangePassword.getFieldsValue()).some(
        (x) => x === null || x === ''
      ) ||
        formEditUser
          .getFieldsError()
          ?.some(({ errors }) => errors?.length > 0) ||
        !isPasswordMatching() ||
        formChangePassword
          .getFieldsError()
          ?.some(({ errors }) => errors?.length > 0)
    );
  };

  return (
    <div>
      {!userLoading ? (
        <div className="settings-form">
          <Form
            form={formEditUser}
            layout="horizontal"
            name="setupAddress"
            autoComplete="off"
            initialValues={initialValues}
            onValuesChange={checkValues}
            {...formItemLayout}
          >
            <Title>Profile</Title>
            <Form.Item
              name="firstName"
              label={<Text>First Name</Text>}
              rules={validationRules.nameOrSurname}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="lastName"
              label={<Text>Last Name</Text>}
              rules={validationRules.nameOrSurname}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="email"
              label={<Text>Email</Text>}
              rules={validationRules.email}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="phoneNumber"
              label={<Text>Phone Number</Text>}
              rules={validationRules.phoneNumber}
            >
              <Input />
            </Form.Item>
            <Form.Item>
              <Button
                onClick={editUserHandler}
                loading={loading}
                type="primary"
                disabled={isSubmitDisabled}
              >
                Confirm
              </Button>
            </Form.Item>
          </Form>
          <Form
            form={formChangePassword}
            layout="horizontal"
            name="setupAddress"
            autoComplete="off"
            onValuesChange={checkPasswordValues}
            {...formItemLayout}
          >
            <Title>Password</Title>
            <Form.Item
              name="password"
              label={<Text>New Password</Text>}
              rules={[
                {
                  required: true,
                  message: 'Please enter your new password',
                },
                {
                  min: 6,
                  max: 256,
                  required: true,
                  message:
                    "Password's length is minimum 6 and maximum 256 characters.",
                },
                {
                  pattern: /^(?=.*?[a-z])(?=.*?[A-Z]).*$/gm,
                  required: true,
                  message:
                    'Password should contain 1 uppercase and 1 lowercase characters.',
                },
              ]}
            >
              <Input.Password
                placeholder="input password"
                iconRender={(visible) =>
                  visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                }
              />
            </Form.Item>
            <Form.Item
              name="confirmPassword"
              validateStatus={
                !isPasswordMatching() ||
                formChangePassword.getFieldsError()[1]?.errors[0]
                  ? 'error'
                  : ''
              }
              help={!isPasswordMatching() ? 'Password does not match' : null}
              label={<Text>Confirm Password</Text>}
              rules={[
                {
                  required: true,
                  message: 'Please confirm your Password',
                },
              ]}
            >
              <Input.Password
                placeholder="input password"
                iconRender={(visible) =>
                  visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                }
              />
            </Form.Item>
            <Form.Item>
              <Button
                onClick={changePasswordHandler}
                type="primary"
                disabled={isChangePasswordDisabled}
              >
                Change Password
              </Button>
            </Form.Item>
          </Form>
          <Form layout="horizontal" {...formItemLayout}>
            <Title>Role</Title>
            <Form.Item label={<Text>Current Role</Text>}>
              <Text strong>
                {currentRole ? formatEnumLabel(currentRole) : '-'}
              </Text>
            </Form.Item>
            <Form.Item label={<Text>New Role</Text>}>
              <Select
                placeholder="Select new role"
                value={newRole}
                onChange={(value: UserRole) => {
                  setNewRole(value);
                  setNewSubtype(undefined);
                }}
                options={ROLE_OPTIONS}
              />
            </Form.Item>
            {newRole === UserRole.ContentCreator && (
              <Form.Item label={<Text>Subtype</Text>}>
                <Select
                  placeholder="Select subtype"
                  value={newSubtype}
                  onChange={(value: AccountSubType) => setNewSubtype(value)}
                  options={Object.values(AccountSubType).map((subtype) => ({
                    value: subtype,
                    label: formatEnumLabel(subtype),
                  }))}
                />
              </Form.Item>
            )}
            <Form.Item>
              <Button
                danger
                type="primary"
                onClick={changeRoleHandler}
                loading={updateRoleLoading}
                disabled={isChangeRoleDisabled}
              >
                Change Role
              </Button>
            </Form.Item>
          </Form>
        </div>
      ) : (
        <div className="spin-wrapper">
          <Spin indicator={<LoadingOutlined style={{ fontSize: 56 }} spin />} />
        </div>
      )}
    </div>
  );
};

export default CustomerSettings;
